import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  ComboboxItem,
  Group,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useDocumentTitle } from "@mantine/hooks";
import { IconPlus, IconRefresh, IconSearch, IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";

import { WithQueueNavbar } from "@layouts/WithQueueNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { showErrorNotification } from "@utils/showNotification";

import { PAGE_TITLE } from "@constants/index";

import { appointmentQKeys } from "@hooks/queries/appointment/queryKeys";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useAppStore } from "@hooks/store/useAppStore";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { useAuth } from "@contexts/auth";

import { CreateAppointmentModal } from "../../Appointments/components/CreateAppointmentModal";
import { HomecareTable } from "../../Appointments/components/HomecareTable";
import { LabAppointmentTable } from "../../Appointments/components/LabAppointmentTable";
import { TeleconsultationTable } from "../../Appointments/components/TeleconsultationAppointmentTable";

import { StyledIncoming } from "./Incoming.styled";


export const IncomingAppointments = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  useDocumentTitle(`${t("queue")} • ${PAGE_TITLE}`);
  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );
  const { roles } = useAuth();

  const [searchFilter, setSearchFilter] = useState("");
  const [status, setStatus] = useState(storeTabs.queueStatusTab);
  const [category, setCategory] = useState(storeTabs.queueCategoryTab);

  const [doctors, setDoctors] = useState<ComboboxItem[]>([]);
  const [opened, { open, close }] = useDisclosure(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { buttonRef } = useRefreshAnimation({ isRefreshing });
  const [currentPage, setCurrentPage] = useState(1);

  const { data: doctorsData } = useGetUsers({ role: "doctor" });

  useEffect(() => {
    if (doctorsData) {
      const newData = doctorsData.map(doctor => ({
        label: doctor.fullName,
        value: doctor.id,
      }));

      setDoctors(newData);
    }
  }, [doctorsData]);

  useEffect(() => {
    // On page load set query params from app store
    if (status) setStatus(status);
    if (category) setCategory(category);


  }, []);

  return (
    <WithSidebar hasBackground hasDashboardNavbar>
      <WithQueueNavbar>
        <StyledIncoming>
          <TabsRoot
            value={category}
            onValueChange={val => {
              setCategory(val);
              setStoreTabs({ queueCategoryTab: val });
              setCurrentPage(1);
            }}>
            <div className="nested-header">
              <Group>
                <TabsList
                  ariaLabel="Incoming Request Type"
                  comboboxItems={[
                    { label: t("homecare"), value: "homecare" },
                    { label: t("teleconsultation"), value: "teleconsultation" },
                    // { label: t("labs"), value: "lab" },
                  ]}
                />
                <Tooltip
                  label={t("refresh-data")}
                  position="right-end"
                  fz={12}
                  events={{ focus: true, hover: true, touch: true }}>
                  <ActionIcon
                    ref={buttonRef}
                    variant={"default"}
                    size={"md"}
                    onClick={() => {
                      queryClient.invalidateQueries(appointmentQKeys.all());
                    }}>
                    <IconRefresh
                      className={"refresh-icon"}
                      color="var(--mantine-color-black)"
                      size={16}
                    />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <div className="wrapper">
                {/* //! NB: TextInput component itself lags when searching  */}
                <TextInput
                  placeholder={t("search-for-patient")}
                  w={{ base: 300, md: 350, lg: 400 }}
                  value={searchFilter as string}
                  onChange={e => {
                    setSearchFilter(e.currentTarget.value.toLocaleLowerCase());
                    setStatus(status);
                    setCategory(category);
                    setCurrentPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                  rightSection={
                    searchFilter && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => setSearchFilter("")}>
                        <IconX size={16} />
                      </ActionIcon>
                    )
                  }
                />
                <CreateAppointmentModal opened={opened} close={close} />
                <PermissionGate permission="queue.edit"><Button leftSection={<IconPlus size={18} />} onClick={open}>
                  {t("create-appointment")}
                </Button></PermissionGate>
              </div>
            </div>
            <TabsContent
              value="teleconsultation"
              style={{ paddingBottom: "7rem" }}>
              <TeleconsultationTable
                searchFilter={searchFilter as string}
                hasAcceptBtn
                variant="incoming"
                category="teleconsultation"
                doctors={doctors}
                setIsRefreshing={setIsRefreshing}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </TabsContent>
            <TabsContent value="homecare" style={{ paddingBottom: "7rem" }}>
              <HomecareTable
                searchFilter={searchFilter as string}
                hasAcceptBtn
                variant="incoming"
                category="homecare"
                doctors={doctors}
                setIsRefreshing={setIsRefreshing}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </TabsContent>
            {/* <TabsContent value="lab" style={{ paddingBottom: "7rem" }}>
              <LabAppointmentTable
                searchFilter={searchFilter as string}
                hasAcceptBtn
                variant="incoming"
                category="lab"
                doctors={doctors}
                setIsRefreshing={setIsRefreshing}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </TabsContent> */}
          </TabsRoot>
        </StyledIncoming>
      </WithQueueNavbar>
    </WithSidebar>
  );
};
